import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// Bulk-email selected invoices one unit at a time. A "unit" is either a single invoice or a billing
// GROUP (several companies under one owner → ONE combined email with all their PDFs). For each unit the
// operator gets a fully pre-filled, editable draft (sender / recipient / cc / subject / body /
// attachments); nothing sends until they click "Send & next". A summary reports sent / failed / skipped.
type Phase = 'confirm' | 'edit' | 'summary';
type Status = 'sent' | 'failed' | 'skipped';
type Result = { label: string; sub: string; status: Status; error?: string };
type Extra = { filename: string; contentBase64: string; contentType?: string };
type Unit = { kind: 'single' | 'group'; invoiceIds: string[]; label: string; sub: string };

const validEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const fileToDataUrl = (f: File) => new Promise<string>((resolve, reject) => {
  const r = new FileReader(); r.onload = () => resolve(String(r.result || '')); r.onerror = reject; r.readAsDataURL(f);
});

// Inline editable list of email chips (used for both To and Cc).
function EmailChips({ value, onChange, ph }: { value: string[]; onChange: (v: string[]) => void; ph: string }) {
  const [q, setQ] = useState('');
  const add = (raw: string) => { const v = raw.trim().toLowerCase(); if (v && validEmail(v) && !value.includes(v)) onChange([...value, v]); setQ(''); };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '6px 8px', border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)' }}>
      {value.map(e => (
        <span key={e} style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 8, background: 'var(--accent-bg)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {e}<span onClick={() => onChange(value.filter(x => x !== e))} style={{ cursor: 'pointer', fontWeight: 800 }}>×</span>
        </span>
      ))}
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={ph}
        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && q.trim()) { e.preventDefault(); add(q); } }}
        onBlur={() => q.trim() && add(q)}
        style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary)' }} />
    </div>
  );
}

export default function BulkInvoiceEmail({ invoices, onClose, onDone, mode = 'fee' }: {
  invoices: Array<{ id: string; client_name?: string }>;
  onClose: () => void; onDone: () => void;
  // 'fee' = the normal invoice email (TPL-019, billing-group aware). 'reminder' = payment reminder
  // (TPL-026) for unpaid invoices — always ONE email per invoice (no group merging).
  mode?: 'fee' | 'reminder';
}) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('confirm');
  const [units, setUnits] = useState<Unit[]>([]);
  const [planErr, setPlanErr] = useState('');
  const [idx, setIdx] = useState(0);
  const [prepBusy, setPrepBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [inlineErr, setInlineErr] = useState('');
  const [warning, setWarning] = useState('');
  const [results, setResults] = useState<Result[]>([]);
  const [copied, setCopied] = useState(false);

  // current editable draft
  const [from, setFrom] = useState('');
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [initialBody, setInitialBody] = useState('');   // seeds the contentEditable (keyed by idx)
  const [headTitle, setHeadTitle] = useState('');
  const [headSub, setHeadSub] = useState('');
  const [attachments, setAttachments] = useState<Array<{ name: string; invoiceId: string }>>([]);
  const [includePdf, setIncludePdf] = useState(true);
  const [extras, setExtras] = useState<Extra[]>([]);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const total = units.length;
  const curUnit = units[idx];

  // Build the send plan (group-aware) when the operator starts.
  // Both flavours are billing-group aware: several companies under one owner become ONE email to the
  // group's primary company (reminders use the group reminder template with every unpaid invoice listed).
  const start = async () => {
    setPlanErr('');
    try {
      const r: any = await api.planBulkEmail(invoices.map(i => i.id));
      const us: Unit[] = (r.units || []).map((u: any) => ({ kind: u.kind, invoiceIds: u.invoiceIds || [], label: u.label || '', sub: u.sub || '' }));
      if (!us.length) { setPlanErr(t('iv_email_need')); return; }
      setUnits(us); setIdx(0); setPhase('edit');
    } catch (e: any) { setPlanErr(e?.message || 'Failed to plan emails'); }
  };

  const loadDraft = async (i: number) => {
    const u = units[i]; if (!u) return;
    setPrepBusy(true); setInlineErr(''); setWarning(''); setIncludePdf(true); setExtras([]);
    try {
      if (u.kind === 'group') {
        const d: any = await api.prepareGroupEmail(u.invoiceIds, mode === 'reminder' ? 'reminder' : undefined);
        setFrom(d.from || ''); setTo(d.to || []); setCc(d.cc || []);
        setSubject(d.subject || ''); setInitialBody(d.bodyHtml || '');
        setHeadTitle(d.groupName || u.label); setHeadSub(d.companyList || u.sub);
        setAttachments(d.attachments || []); setWarning(d.warning || '');
      } else {
        const d: any = await api.prepareInvoiceEmail(u.invoiceIds[0], undefined, mode === 'reminder' ? 'reminder' : undefined);
        setFrom(d.from || ''); setTo(d.to || []); setCc(d.cc || []);
        setSubject(d.subject || ''); setInitialBody(d.bodyHtml || '');
        setHeadTitle(d.docNo || u.label); setHeadSub(d.clientName || u.sub);
        setAttachments([{ name: d.attachmentName || '', invoiceId: u.invoiceIds[0] }]); setWarning(d.warning || '');
      }
    } catch (e: any) {
      setFrom(''); setTo([]); setCc([]); setSubject(''); setInitialBody('');
      setHeadTitle(u.label); setHeadSub(u.sub); setAttachments([]);
      setInlineErr(e?.message || 'Failed to prepare email');
    } finally { setPrepBusy(false); }
  };

  useEffect(() => { if (phase === 'edit') loadDraft(idx); }, [phase, idx]); // eslint-disable-line

  const record = (status: Status, error?: string) => setResults(prev => [...prev, { label: headTitle, sub: headSub, status, error }]);
  const advance = () => { if (idx + 1 >= total) setPhase('summary'); else setIdx(idx + 1); };

  const doSend = async () => {
    const body = bodyRef.current ? bodyRef.current.innerHTML : initialBody;
    if (!to.length) { setInlineErr(t('iv_email_need')); return; }
    setBusy(true); setInlineErr('');
    try {
      if (curUnit.kind === 'group') {
        await api.sendGroupEmailCustom({ ids: curUnit.invoiceIds, from, to, cc, subject, bodyHtml: body, includePdf, attachments: extras });
      } else {
        await api.sendInvoiceEmailCustom(curUnit.invoiceIds[0], { from, to, cc, subject, bodyHtml: body, includePdf, attachments: extras });
      }
      record('sent'); advance();
    } catch (e: any) {
      setInlineErr(e?.message || 'Failed to send'); // stay on this unit so the operator can fix & retry
    } finally { setBusy(false); }
  };
  const doSkip = () => { record(inlineErr ? 'failed' : 'skipped', inlineErr || undefined); advance(); };

  const onAddFiles = async (files: FileList | null) => {
    if (!files) return;
    const added: Extra[] = [];
    for (const f of Array.from(files)) added.push({ filename: f.name, contentBase64: await fileToDataUrl(f), contentType: f.type || undefined });
    setExtras(prev => [...prev, ...added]);
  };

  const sentN = results.filter(r => r.status === 'sent').length;
  const failN = results.filter(r => r.status === 'failed').length;
  const skipN = results.filter(r => r.status === 'skipped').length;
  const notSent = results.filter(r => r.status !== 'sent');
  const copyDetails = () => {
    const text = notSent.map(r => `${r.label} — ${r.sub || '—'} [${r.status}]${r.error ? `: ${r.error}` : ''}`).join('\n');
    navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); }).catch(() => {});
  };

  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', display: 'block', marginBottom: 5 };
  const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
  const overlay = (children: React.ReactNode, width = 660) => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10003, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-md)' }}>{children}</div>
    </div>
  );

  // ── Phase 1: confirm / reminder ────────────────────────────────────────────
  if (phase === 'confirm') return overlay(
    <>
      <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>{t(mode === 'reminder' ? 'bie_remind_title' : 'bie_confirm_title')}</div>
      <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 12, lineHeight: 1.6 }}>{t(mode === 'reminder' ? 'bie_remind_body' : 'bie_confirm_body').replace('{n}', String(invoices.length))}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginTop: 8, lineHeight: 1.6 }}>{t('bie_group_note')}</div>
      {planErr && <div style={{ fontSize: 12.5, color: '#E24B4A', background: '#FCEBEB', borderRadius: 9, padding: '8px 11px', marginTop: 12 }}>{planErr}</div>}
      <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
        <button onClick={start} style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 13.5, cursor: 'pointer' }}>{t('bie_start')}</button>
      </div>
    </>, 480);

  // ── Phase 3: summary ───────────────────────────────────────────────────────
  if (phase === 'summary') return overlay(
    <>
      <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>{t('bie_summary_title')}</div>
      {sentN > 0 && <div style={{ fontSize: 12.5, fontWeight: 700, color: '#B45309', background: '#FEF3C7', border: '1px solid #FBD38D', borderRadius: 9, padding: '8px 12px', marginTop: 10 }}><i className="ti ti-mail-pause" style={{ marginRight: 6 }} />{t('oe_queued_bulk', { n: sentN })}</div>}
      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, padding: '5px 12px', borderRadius: 9, background: '#E7F6EE', color: '#15803D' }}><i className="ti ti-circle-check" style={{ marginRight: 6 }} />{t('bie_status_sent')}: {sentN}</span>
        {failN > 0 && <span style={{ fontSize: 13, fontWeight: 700, padding: '5px 12px', borderRadius: 9, background: '#FCEBEB', color: '#E24B4A' }}><i className="ti ti-circle-x" style={{ marginRight: 6 }} />{t('bie_status_failed')}: {failN}</span>}
        {skipN > 0 && <span style={{ fontSize: 13, fontWeight: 700, padding: '5px 12px', borderRadius: 9, background: 'var(--c-gray-100)', color: 'var(--text-secondary)' }}>{t('bie_status_skipped')}: {skipN}</span>}
        <span style={{ fontSize: 13, color: 'var(--text-tertiary)', alignSelf: 'center' }}>/ {total}</span>
      </div>
      {notSent.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{t('bie_failed_head')}</div>
            <button onClick={copyDetails} style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
              <i className="ti ti-copy" style={{ marginRight: 5 }} />{copied ? t('bie_copied') : t('bie_copy')}
            </button>
          </div>
          <div style={{ marginTop: 10, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            {notSent.map((r, i) => (
              <div key={i} style={{ padding: '9px 12px', borderBottom: i < notSent.length - 1 ? '1px solid var(--border)' : 'none', background: r.status === 'failed' ? '#FFF8F8' : 'transparent' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{r.label} · {r.sub || '—'}
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: r.status === 'failed' ? '#E24B4A' : 'var(--text-tertiary)' }}>{t(r.status === 'failed' ? 'bie_status_failed' : 'bie_status_skipped')}</span>
                </div>
                {r.error && <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 3 }}>{r.error}</div>}
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 8 }}>{t('bie_failed_hint')}</div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
        <button onClick={() => { onDone(); onClose(); }} style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 13.5, cursor: 'pointer' }}>{t('bie_done')}</button>
      </div>
    </>, 560);

  // ── Phase 2: per-unit editor ───────────────────────────────────────────────
  return overlay(
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{t('iv_email_title')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {curUnit?.kind === 'group' && <span style={{ fontSize: 11.5, fontWeight: 800, color: '#5B21B6', background: '#EDE9FE', padding: '4px 10px', borderRadius: 999 }}><i className="ti ti-users" style={{ marginRight: 4 }} />{t('bie_group_badge')}</span>}
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-bg)', padding: '4px 11px', borderRadius: 999 }}>
            {t('bie_progress').replace('{i}', String(idx + 1)).replace('{n}', String(total))}
          </div>
        </div>
      </div>
      {(headTitle || headSub) && <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginTop: 4 }}>{headTitle}{headSub ? ` · ${headSub}` : ''}</div>}

      {prepBusy ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} /></div>
      ) : (
        <div style={{ marginTop: 14 }}>
          {warning && <div style={{ fontSize: 12.5, color: '#92400E', background: '#FEF3C7', borderRadius: 9, padding: '8px 11px', marginBottom: 12 }}><i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />{warning}</div>}

          <label style={lbl}>{t('bie_from')}</label>
          <input value={from} onChange={e => setFrom(e.target.value)} style={inp} />

          <label style={{ ...lbl, marginTop: 13 }}>{t('bie_to')}</label>
          <EmailChips value={to} onChange={setTo} ph={t('bie_email_ph')} />

          <label style={{ ...lbl, marginTop: 13 }}>{t('bie_cc')}</label>
          <EmailChips value={cc} onChange={setCc} ph={t('bie_email_ph')} />

          <label style={{ ...lbl, marginTop: 13 }}>{t('bie_subject')}</label>
          <input value={subject} onChange={e => setSubject(e.target.value)} style={inp} />

          <label style={{ ...lbl, marginTop: 13 }}>{t('bie_body')}</label>
          <div key={idx} ref={bodyRef} contentEditable suppressContentEditableWarning
            dangerouslySetInnerHTML={{ __html: initialBody }}
            style={{ minHeight: 150, maxHeight: 280, overflowY: 'auto', padding: '11px 13px', fontSize: 13.5, lineHeight: 1.6, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none' }} />

          <label style={{ ...lbl, marginTop: 13 }}>{t('bie_attachment')}</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {attachments.length > 0 && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={includePdf} onChange={e => setIncludePdf(e.target.checked)} />
                <i className="ti ti-file-type-pdf" style={{ color: '#E24B4A', fontSize: 16 }} />
                <span style={{ fontWeight: 600 }}>{attachments.length === 1 ? attachments[0].name : t('bie_n_pdfs').replace('{n}', String(attachments.length))}</span>
              </label>
            )}
            {attachments.length > 1 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingLeft: 27 }}>
                {attachments.map((a, i) => (
                  <span key={i} onClick={() => api.openInvoicePdf(a.invoiceId).catch(() => {})} style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--accent)', cursor: 'pointer', background: 'var(--accent-bg)', borderRadius: 7, padding: '2px 8px' }}>{a.name}</span>
                ))}
              </div>
            )}
            {attachments.length === 1 && (
              <span onClick={() => api.openInvoicePdf(attachments[0].invoiceId).catch(() => {})} style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 600, cursor: 'pointer', paddingLeft: 27 }}>{t('bie_preview_pdf')}</span>
            )}
            {extras.map((x, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: 'var(--text-primary)' }}>
                <i className="ti ti-paperclip" style={{ fontSize: 15, color: 'var(--text-tertiary)' }} />
                <span style={{ fontWeight: 600 }}>{x.filename}</span>
                <span onClick={() => setExtras(prev => prev.filter((_, j) => j !== i))} style={{ cursor: 'pointer', color: '#E24B4A', fontWeight: 800, marginLeft: 2 }}>×</span>
              </div>
            ))}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--accent)', cursor: 'pointer', width: 'fit-content' }}>
              <i className="ti ti-plus" />{t('bie_add_file')}
              <input type="file" multiple style={{ display: 'none' }} onChange={e => { onAddFiles(e.target.files); e.target.value = ''; }} />
            </label>
          </div>

          {inlineErr && <div style={{ fontSize: 12.5, color: '#E24B4A', background: '#FCEBEB', borderRadius: 9, padding: '8px 11px', marginTop: 13 }}><i className="ti ti-alert-circle" style={{ marginRight: 6 }} />{inlineErr}</div>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={() => { if (window.confirm(t('bie_cancel_confirm'))) (results.length ? setPhase('summary') : onClose()); }} style={{ padding: '9px 16px', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('bie_cancel_all')}</button>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={doSkip} disabled={busy} style={{ padding: '9px 18px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('bie_skip')}</button>
          <button onClick={doSend} disabled={busy || prepBusy} style={{ padding: '9px 24px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 13.5, cursor: busy ? 'default' : 'pointer', opacity: busy || prepBusy ? 0.7 : 1 }}>
            {busy ? <><i className="ti ti-loader" style={{ marginRight: 6 }} />{t('bie_sending')}</> : <><i className="ti ti-send" style={{ marginRight: 6 }} />{t('bie_send_next')}</>}
          </button>
        </div>
      </div>
    </>, 680);
}
